import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';

import { makeAuthenticatedApiCall } from '../../../apiHelper';

interface ResendDealModalProps {
  pipedriveDealId: number;
  regPlate: string;
  isProspection?: boolean;
}

const ResendDealModal: React.FC<ResendDealModalProps> = ({
  pipedriveDealId,
  regPlate,
  isProspection = false,
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleOpen = () => {
    setOpen(true);
    setError(null);
  };

  const handleClose = () => {
    if (!loading) {
      setOpen(false);
      setError(null);
    }
  };

  const handleConfirm = async () => {
    setLoading(true);
    setError(null);

    try {
      // Récupérer les données complètes du deal pour le pré-remplissage
      const response = await makeAuthenticatedApiCall(
        'get',
        `/api/deal/${pipedriveDealId}/resend-data${isProspection ? '?prospection=true' : ''}`,
      );

      if (response.status === 200 && response.data) {
        // Stocker les données dans sessionStorage pour les récupérer dans CreateCotation
        sessionStorage.setItem('resendDealData', JSON.stringify(response.data));
        sessionStorage.setItem('originalDealId', String(pipedriveDealId));
        sessionStorage.setItem('isResendMode', 'true');

        // Naviguer vers CreateCotation
        navigate('/cotation');
      } else {
        setError('Erreur lors de la récupération des données du deal');
      }
    } catch (err: any) {
      console.error('Error fetching deal data for resend:', err);
      setError(err.response?.data?.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleOpen}
        sx={{
          marginTop: 1,
          borderColor: '#ff9800',
          color: '#ff9800',
          '&:hover': {
            borderColor: '#f57c00',
            backgroundColor: 'rgba(255, 152, 0, 0.04)',
          },
        }}
      >
        Renvoyer ce deal
      </Button>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="resend-deal-dialog-title"
        aria-describedby="resend-deal-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="resend-deal-dialog-title" sx={{ fontSize: 20, fontWeight: 600 }}>
          Renvoyer ce deal
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="resend-deal-dialog-description" sx={{ color: 'text.primary' }}>
            <Typography variant="body1" sx={{ marginBottom: 2 }}>
              Vous allez créer une <strong>nouvelle cotation</strong> basée sur le deal{' '}
              <strong>{regPlate}</strong>.
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: 2 }}>
              Cette action va :
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              <li>
                <Typography variant="body2" component="span">
                  Ouvrir le formulaire de cotation avec toutes les données pré-remplies (véhicule, dommages, photos,
                  etc.)
                </Typography>
              </li>
              <li>
                <Typography variant="body2" component="span">
                  Vous permettre de modifier les informations si nécessaire
                </Typography>
              </li>
              <li>
                <Typography variant="body2" component="span">
                  Créer un nouveau deal lors de la soumission
                </Typography>
              </li>
              <li>
                <Typography variant="body2" component="span">
                  Marquer le deal actuel comme <strong>clôturé</strong>
                </Typography>
              </li>
            </ul>
          </DialogContentText>
          {error && (
            <Typography color="error" sx={{ marginTop: 2 }}>
              {error}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading} className="secondaryDealActionButton">
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading}
            variant="contained"
            className="dealActionButton"
            sx={{
              backgroundColor: '#ff9800',
              '&:hover': {
                backgroundColor: '#f57c00',
              },
            }}
          >
            {loading ? <CircularProgress size={24} /> : 'Confirmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ResendDealModal;
